import { useEffect, useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { IconScale } from "@tabler/icons-react";
import { useApiContext, API_BASE, handleResponse } from "@/contexts/ApiContext";
import { clearPendingLegalAcceptance, currentLegal, pendingLegalAcceptance } from "@/lib/legal";
import { testIds } from "@/testIds";
import { BetaBanner } from "./BetaBanner";

const T = testIds.legalNotice;

interface LegalAcceptance {
  version: number | null;
  acceptedAt: string | null;
}

/**
 * Which legal version this account accepted, and whether the notice is due (launch redesign 2c).
 * A version accepted at sign-up (the terms box) and not saved yet is saved first, silently: the
 * account only exists once sign-in completes, often in another tab (the verification email).
 */
export function useLegalNotice() {
  const { fetchWithAuth } = useApiContext();
  const queryClient = useQueryClient();
  const current = currentLegal();
  const accepted = useQuery({
    queryKey: ["me", "legal"],
    queryFn: async () => handleResponse<LegalAcceptance>(await fetchWithAuth(`${API_BASE}/me/legal`)),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
  const accept = useMutation({
    mutationFn: async (version: number) =>
      handleResponse<LegalAcceptance>(
        await fetchWithAuth(`${API_BASE}/me/legal`, { method: "POST", body: JSON.stringify({ version }) }),
      ),
    onSuccess: (saved) => queryClient.setQueryData(["me", "legal"], saved),
  });

  const pending = pendingLegalAcceptance();
  const savedVersion = accepted.data?.version ?? null;
  useEffect(() => {
    if (!accepted.isSuccess || pending == null || accept.isPending) return;
    if (savedVersion != null && savedVersion >= pending) { clearPendingLegalAcceptance(); return; }
    accept.mutate(pending, { onSuccess: () => clearPendingLegalAcceptance() });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per load of the acceptance
  }, [accepted.isSuccess, savedVersion, pending]);

  const due = accepted.isSuccess && pending == null && (savedVersion == null || savedVersion < current.version);
  return {
    due,
    /** Never accepted anything (an account from before 16 Oct 2026), or accepted an older version. */
    kind: savedVersion == null ? ("published" as const) : ("updated" as const),
    effective: current.effective,
    ok: () => accept.mutate(current.version),
    saving: accept.isPending,
  };
}

/**
 * One line at the top of the signed-in pages when the Terms or Privacy Policy changed since the
 * account accepted them (launch redesign 2c). It doesn't block work; OK records the version.
 * It takes the beta banner's place and publishes its height as `--banner-h` in the same way, so
 * full-height pages still end at the window's edge.
 */
export function LegalUpdateNotice({ notice }: { notice: ReturnType<typeof useLegalNotice> }) {
  const { t, i18n } = useTranslation("shell");
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const root = document.documentElement;
    const el = ref.current;
    if (!el) return;
    const publish = () => root.style.setProperty("--banner-h", `${el.offsetHeight}px`);
    publish();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(publish);
    observer?.observe(el);
    return () => {
      observer?.disconnect();
      root.style.setProperty("--banner-h", "0px");
    };
  }, []);

  const date = notice.effective
    ? new Intl.DateTimeFormat(i18n.language || "en", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" })
        .format(new Date(`${notice.effective}T00:00:00Z`))
    : "";

  return (
    <div
      ref={ref}
      data-testid={T.root}
      data-kind={notice.kind}
      role="status"
      aria-live="polite"
      className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 border-b border-[var(--lilia-border)] bg-[var(--lilia-surface-2,var(--lilia-surface))] px-4 py-1.5 text-xs text-[var(--lilia-text)]"
    >
      <IconScale size={14} className="shrink-0 text-[var(--lilia-text-secondary)]" aria-hidden />
      <span>{t(`legalNotice.${notice.kind}`, { date })}</span>
      <Link data-testid={T.see} to="/legal/terms" className="font-medium text-[var(--lilia-accent)] underline-offset-2 hover:underline">
        {t(notice.kind === "updated" ? "legalNotice.seeChanges" : "legalNotice.read")}
      </Link>
      <button
        type="button"
        data-testid={T.ok}
        disabled={notice.saving}
        onClick={notice.ok}
        className="rounded border border-[var(--lilia-border)] px-2 py-0.5 font-medium hover:bg-[var(--lilia-hover)] disabled:opacity-60"
      >
        {t("legalNotice.ok")}
      </button>
    </div>
  );
}

/**
 * The top line of the signed-in pages: the legal notice when it is due, otherwise the beta banner.
 * Mounted on every signed-in page so a version accepted at sign-up is saved wherever the author
 * lands; the line itself only where the beta banner would show (not in the editor).
 */
export function TopNotice({ showLine }: { showLine: boolean }) {
  const notice = useLegalNotice();
  if (!showLine) return null;
  return notice.due ? <LegalUpdateNotice notice={notice} /> : <BetaBanner />;
}
